const CHROMIUM_BRAND_RE = /Chrome\/|Chromium\/|Edg\/|OPR\/|SamsungBrowser\//;

/**
 * Chromium ships "AppleWebKit" and "Safari" in its UA, so it is ruled out by brand first;
 * Firefox matches neither. iOS builds of other browsers (CriOS/EdgiOS/FxiOS) are left in —
 * every iOS browser runs on WebKit.
 */
function isWebKitEngine(): boolean {
  const { userAgent } = navigator;
  return !CHROMIUM_BRAND_RE.test(userAgent) && /Safari\//.test(userAgent);
}

/**
 * Redirect is preferred over popup, which mobile browsers routinely block, leaving users
 * unable to log in. Popup is the fallback wherever redirect cannot complete.
 *
 * ```
 * | Environment              | Method   | Why                                             |
 * | ------------------------ | -------- | ----------------------------------------------- |
 * | production               | redirect | auth handler proxied onto our own origin        |
 * | staging (Chrome/Firefox) | redirect | cross-origin auth iframe keeps its storage      |
 * | staging (WebKit)         | popup    | WebKit partitions that iframe, redirect is lost |
 * | local dev                | popup    | redirect needs https                            |
 * ```
 *
 * Firebase authorized domains take no wildcards, so the ephemeral `pr-<n>.staging.corca.app`
 * hosts cannot each have their own auth handler: staging shares one at `auth.staging.corca.app`
 * and the session returns through a hidden iframe there. A popup is a top-level window on that
 * domain instead, so partitioning does not reach it. Production has a single known host and
 * proxies the handler onto it (PROXY_FIREBASE_AUTH_DOMAIN).
 *
 * To exercise redirect locally, run `HTTPS=true pnpm dev` with:
 * ```
 * NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN=localhost:3000
 * PROXY_FIREBASE_AUTH_DOMAIN=auth.staging.corca.app
 * ```
 */
export function canSignInWithRedirect(authDomain: string): boolean {
  if (typeof window === "undefined" || window.location.protocol !== "https:") return false;

  const currentHost = window.location.hostname;
  const authHost = authDomain.split(":")[0];
  if (currentHost === authHost) return true;

  if (process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === "staging" && isWebKitEngine()) return false;

  const getSite = (host: string) => {
    const parts = host.split(".");
    return parts.length >= 2 ? parts.slice(-2).join(".") : host;
  };

  return getSite(currentHost) === getSite(authHost);
}
